import React, { useState } from "react";
import HelpBubble from "./HelpBubble";
import { EXPLAIN_HELP } from "./helpTexts";
import { previewPricing } from "../../../api/marketplacePricingApi";
import { RULE_MODE_LABELS, SKIP_REASON_LABELS, formatApiError, money, splitCodes } from "./pricingFormat";

const TOTAL_STEPS = new Set(["required_net", "computed_price"]);

function ExplainLadder({ steps }) {
    return (
        <table className="mp-ladder" aria-label="Расчёт цены">
            <tbody>
                {steps.map((step) => {
                    const className = step.key === "final"
                        ? "is-final"
                        : TOTAL_STEPS.has(step.key)
                            ? "is-total"
                            : step.key === "rule"
                                ? "is-rule"
                                : "";
                    const value = step.key === "commission_divide" ? `${money(step.value)} %` : money(step.value);
                    return (
                        <tr key={step.key} className={className}>
                            <td>{step.label}</td>
                            <td>{value}</td>
                            <td>{step.note || ""}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

function ProfitLine({ item }) {
    if (item.profit_uah_actual === undefined) return null;
    return (
        <div className="mp-stats">
            <span>Безубыточность: <strong>{money(item.breakeven)}</strong></span>
            <span>Прибыль после комиссий: <strong>{money(item.profit_uah_actual)}</strong></span>
            {item.profit_uah_planned !== undefined ? (
                <span>План: <strong>{money(item.profit_uah_planned)}</strong></span>
            ) : null}
        </div>
    );
}

function ExplainItem({ item }) {
    const ready = item.status === "ready";
    const rule = item.rule;
    return (
        <article className="mp-explain-item">
            <div className="mp-explain-head">
                <h4>{item.product_code}</h4>
                {item.brand ? <span className="mp-pill">{item.brand}</span> : null}
                {ready ? (
                    <span className="mp-pill mp-pill-good">Цена в фиде {money(item.price)} грн</span>
                ) : (
                    <span className="mp-pill mp-pill-danger">{item.status === "review" ? "Нужна проверка" : "Не выгружается"}</span>
                )}
                {ready && item.supplier_code ? (
                    <span className="mp-muted">
                        Отправитель {item.supplier_code}{item.supplier_name ? ` · ${item.supplier_name}` : ""} · остаток {item.stock}
                    </span>
                ) : null}
            </div>

            {!ready ? (
                <div className="mp-callout mp-callout-error">{(item.issues || []).join("; ")}</div>
            ) : (
                <>
                    {item.below_breakeven ? (
                        <div className="mp-callout mp-callout-warn" role="note">
                            Цена ниже точки безубыточности: после комиссий остаётся {money(item.profit_uah_actual)} грн
                            {item.rrp_applied ? " (цена = РРЦ поставщика)" : ""}
                            {item.dumping ? " (включён демпинг)" : ""}.
                        </div>
                    ) : null}
                    {rule ? (
                        <div className={rule.applied ? "mp-callout mp-callout-ok" : "mp-callout"}>
                            Правило {rule.scope_type === "brand" ? "бренда" : "товара"} {rule.scope_value}:{" "}
                            {RULE_MODE_LABELS[rule.mode] || rule.mode} {money(rule.value)}
                            {rule.applied ? " — применено." : ` — не применено (${SKIP_REASON_LABELS[rule.skipped_reason] || rule.skipped_reason}).`}
                        </div>
                    ) : null}
                    {item.steps?.length ? <ExplainLadder steps={item.steps} /> : null}
                    <ProfitLine item={item} />
                </>
            )}

            {item.candidates?.length > 1 ? (
                <details>
                    <summary>Все предложения ({item.candidates.length})</summary>
                    <div className="mp-table-scroll">
                        <table className="mp-table">
                            <thead>
                                <tr>
                                    <th>Отправитель</th>
                                    <th className="mp-num">Цена</th>
                                    <th className="mp-num">Остаток</th>
                                    <th className="mp-num">Приоритет / ярус</th>
                                    <th className="mp-num">Прибыль</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {item.candidates.map((candidate) => (
                                    <tr key={candidate.supplier_code} className={candidate.selected ? "selected" : ""}>
                                        <td>{candidate.supplier_code} {candidate.supplier_name || ""}</td>
                                        <td className="mp-num">{money(candidate.price)}</td>
                                        <td className="mp-num">{candidate.stock}</td>
                                        <td className="mp-num">{candidate.priority} / {candidate.tier ?? 0}</td>
                                        <td className="mp-num">{money(candidate.profit_uah_actual)}</td>
                                        <td>
                                            {candidate.selected ? <span className="mp-pill mp-pill-good">победитель</span> : null}
                                            {candidate.below_breakeven ? <span className="mp-pill mp-pill-warn">ниже безуб.</span> : null}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </details>
            ) : null}
        </article>
    );
}

export default function PriceExplainPanel({ platform, accountId, settings }) {
    const [input, setInput] = useState("");
    const [simulate, setSimulate] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const accountMode = settings?.pricing_mode || "legacy";
    const canSimulate = Boolean(settings?.exists);

    const submit = async (event) => {
        event.preventDefault();
        const codes = splitCodes(input);
        if (!codes.length || codes.length > 50) {
            setError("Введите от 1 до 50 Master SKU");
            return;
        }
        setLoading(true);
        setError("");
        try {
            setData(await previewPricing(platform, accountId, codes, simulate || null));
        } catch (apiError) {
            setError(formatApiError(apiError, "Не удалось рассчитать цену."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="mp-card" aria-label="Разбор цены">
            <HelpBubble title="Разбор цены">{EXPLAIN_HELP}</HelpBubble>
            <div className="mp-card-head">
                <div>
                    <h3 className="mp-title">Разбор цены</h3>
                    <p className="mp-lead">
                        Показывает, из чего складывается цена товара в фиде, и сколько остаётся после комиссий. Используются
                        сохранённые настройки.
                    </p>
                </div>
            </div>
            <form className="mp-actions" onSubmit={submit}>
                <input
                    className="mp-input"
                    style={{ flex: "1 1 260px" }}
                    aria-label="Master SKU"
                    placeholder="SKU через пробел или запятую, до 50"
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                />
                <select
                    className="mp-select"
                    style={{ width: "auto" }}
                    aria-label="Режим расчёта"
                    value={simulate}
                    onChange={(event) => setSimulate(event.target.value)}
                >
                    <option value="">Как сейчас на аккаунте ({accountMode === "breakeven" ? "от безубыточности" : "старая цена"})</option>
                    <option value="legacy">Старая цена</option>
                    <option value="breakeven" disabled={!canSimulate}>От безубыточности{canSimulate ? "" : " (сохраните профиль)"}</option>
                </select>
                <button type="submit" className="mp-btn mp-btn-primary" disabled={loading || !input.trim()}>
                    {loading ? "Считаем…" : "Разобрать цену"}
                </button>
            </form>
            {error ? <div className="mp-callout mp-callout-error" role="alert">{error}</div> : null}
            {data ? (
                <>
                    <p className="mp-muted">
                        Режим расчёта: {data.pricing_mode === "breakeven" ? "от безубыточности" : "старая цена"}. Выбор отправителя:{" "}
                        {data.selection_rule}.
                    </p>
                    {data.items.map((item) => <ExplainItem key={item.product_code} item={item} />)}
                </>
            ) : null}
        </section>
    );
}
